import React, { useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Ixoraly',
  url: 'https://ixoraly.com',
  logo: 'https://customer-assets.emergentagent.com/job_pr-platform-4/artifacts/z6ztu1ue_Transparent%20background%20-%20Copy.png',
  description: 'Professional Press Release Distribution Platform',
  contactPoint: { '@type': 'ContactPoint', telephone: '+91-20-6687-2534', contactType: 'customer service', email: 'contact@ixoraly.com' },
  sameAs: []
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Ixoraly',
  url: 'https://ixoraly.com',
  potentialAction: { '@type': 'SearchAction', target: 'https://ixoraly.com/newsroom?q={search_term_string}', 'query-input': 'required name=search_term_string' }
};

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about-us' },
    { name: 'Newsroom', href: '/newsroom' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (href) => location.pathname === href;

  useEffect(() => {
    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.setAttribute('data-layout-jsonld', 'org');
    orgScript.textContent = JSON.stringify(organizationSchema);
    document.head.appendChild(orgScript);

    const webScript = document.createElement('script');
    webScript.type = 'application/ld+json';
    webScript.setAttribute('data-layout-jsonld', 'web');
    webScript.textContent = JSON.stringify(websiteSchema);
    document.head.appendChild(webScript);

    return () => { orgScript.remove(); webScript.remove(); };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top Bar */}
      <div className="bg-navy-900 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center space-x-6">
            <span className="flex items-center space-x-2">
              <span className="text-gray-300">📧</span>
              <a href="mailto:contact@ixoraly.com" className="hover:text-coral-500 transition-colors">contact@ixoraly.com</a>
            </span>
            <span className="hidden sm:flex items-center space-x-2">
              <span className="text-gray-300">📞</span>
              <a href="tel:+912066872534" className="hover:text-coral-500 transition-colors">+91 20 6687 2534</a>
            </span>
          </div>
          <Link to="/submit-press-release" className="text-coral-500 hover:text-coral-400 font-medium transition-colors">
            Submit Press Release →
          </Link>
        </div>
      </div>

      {/* Main Header */}
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Ixoraly Logo" 
                className="h-14 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-navy-900 tracking-tight">Ixoraly</span>
                <span className="text-xs text-gray-600 -mt-1">Press Release Distribution</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-12">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-base font-semibold transition-colors duration-200 relative group whitespace-nowrap ${
                    isActive(item.href)
                      ? 'text-navy-600'
                      : 'text-gray-700 hover:text-navy-600'
                  }`}
                >
                  {item.name}
                  <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-navy-600 transform origin-left transition-transform duration-200 ${
                    isActive(item.href) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}></span>
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block ml-8">
              <Link
                to="/pricing"
                className="inline-flex items-center px-6 py-2.5 bg-navy-600 text-white rounded-md font-semibold hover:bg-navy-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Get Started
                <ChevronRight size={18} className="ml-1" />
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden pb-4 pt-2 border-t border-gray-200" data-testid="mobile-menu">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block px-4 py-3 text-base font-medium rounded-md transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-navy-600 bg-navy-50'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="block mx-4 mt-3 px-4 py-2.5 bg-navy-600 text-white text-center rounded-md font-semibold hover:bg-navy-700 transition-colors"
              >
                Get Started
              </Link>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            {/* Company Info - Takes 5 columns */}
            <div className="lg:col-span-5">
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src={LOGO_URL} 
                  alt="Ixoraly Logo" 
                  className="h-14 w-auto"
                  style={{ display: 'block' }}
                  onError={(e) => {
                    console.log('Logo failed to load');
                    e.target.style.display = 'none';
                  }}
                />
                <div className="flex flex-col">
                  <span className="text-2xl font-bold">Ixoraly</span>
                  <span className="text-xs text-gray-400 -mt-1">Press Release Distribution</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-md">
                Professional press release distribution platform connecting businesses with journalists, media outlets, and audiences worldwide.
              </p>
              <div className="flex space-x-4 mb-6">
                <a 
                  href="https://x.com/ixoralyconnect" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-navy-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-navy-700 hover:text-white transition-all duration-200"
                  aria-label="Follow us on Twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/ixoraly/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-navy-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-navy-700 hover:text-white transition-all duration-200"
                  aria-label="Follow us on LinkedIn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Navigation Links - Takes 7 columns split into 3 sections */}
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
              {/* Quick Links */}
              <div>
                <h3 className="font-bold text-base text-white mb-4 uppercase tracking-wide">Quick Links</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link to="/about-us" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      About Us
                    </Link>
                  </li>
                  <li>
                    <Link to="/newsroom" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Newsroom
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Pricing
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Services */}
              <div>
                <h3 className="font-bold text-base text-white mb-4 uppercase tracking-wide">Services</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/submit-press-release" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Submit Press Release
                    </Link>
                  </li>
                  <li>
                    <Link to="/media-distribution" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Media Distribution
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      PR Packages
                    </Link>
                  </li>
                  <li>
                    <Link to="/analytics-reports" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Analytics & Reports
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Legal & Support */}
              <div>
                <h3 className="font-bold text-base text-white mb-4 uppercase tracking-wide">Legal</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms-conditions" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Terms & Conditions
                    </Link>
                  </li>
                  <li>
                    <Link to="/cookie-policy" className="text-gray-400 hover:text-white transition-colors text-sm block">
                      Cookie Policy
                    </Link>
                  </li>
                  <li>
                    <a href="https://ixoraly.com/api/rss/feed.xml" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-1.5" data-testid="footer-rss-link">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11a9 9 0 0 1 9 9"/><path d="M4 4a16 16 0 0 1 16 16"/><circle cx="5" cy="19" r="1"/></svg>
                      RSS Feed
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact Info Bar */}
          <div className="border-t border-navy-800 pt-8 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-navy-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-coral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Address</h4>
                  <p className="text-gray-400 leading-relaxed">
                    N 102 Madhuvanti, Nanded City,<br/>
                    Sinhgad Road, Pune 411 041, India
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-navy-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-coral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Email</h4>
                  <a href="mailto:contact@ixoraly.com" className="text-gray-400 hover:text-white transition-colors">
                    contact@ixoraly.com
                  </a>
                  <br/>
                  <a href="mailto:support@ixoraly.com" className="text-gray-400 hover:text-white transition-colors">
                    support@ixoraly.com
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-navy-800 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-coral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Phone</h4>
                  <a href="tel:+912066872534" className="text-gray-400 hover:text-white transition-colors">
                    +91 20 6687 2534
                  </a>
                  <p className="text-gray-500 text-xs mt-1">Mon-Fri 9AM-6PM IST</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-navy-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
              <p>&copy; 2026 Ixoraly. All rights reserved.</p>
              <p className="mt-2 md:mt-0">Made with ❤️ for brands that matter</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
